import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { FileText, Download } from 'lucide-react';
import { formatTime } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable component to display Request Details for any ticket
 * Shows type-specific fields, attachments, and photo
 */
export const RequestDetailsSection = ({ ticket, compact = false }) => {
  if (!ticket) return null;

  const hasDetails = 
    ticket.courier_type || 
    ticket.courier_pickup_point ||
    ticket.guest_name || 
    ticket.event_name || 
    ticket.room_type || 
    ticket.stationery_items ||
    ticket.stationery_selected_items ||
    ticket.stationery_items_list ||
    ticket.stationery_type ||
    ticket.induction_employee_name ||
    ticket.induction_food_required ||
    ticket.insurance_employee_name ||
    (ticket.insurance_employees && ticket.insurance_employees.length > 0) ||
    (ticket.insurance_add_employees && ticket.insurance_add_employees.length > 0) ||
    ticket.promo_employee_name ||
    (ticket.promo_employees && ticket.promo_employees.length > 0) ||
    ticket.birthday_employee_name ||
    (ticket.birthday_employees && ticket.birthday_employees.length > 0) ||
    ticket.maintenance_category ||
    ticket.other_request_type ||
    ticket.cfv_guest_name || ticket.cfv_company || (ticket.cfv_visitors && ticket.cfv_visitors.length > 0) || (ticket.cfv_visit_locations && ticket.cfv_visit_locations.length > 0) ||
    (ticket.custom_fields && Object.keys(ticket.custom_fields).length > 0) ||
    (ticket.informed_departments && ticket.informed_departments.length > 0) ||
    (ticket.attachments && ticket.attachments.length > 0);

  if (!hasDetails) return null;

  const DetailRow = ({ label, value }) => {
    if (!value) return null;
    return (
      <div className={`flex justify-between items-start py-2 border-b border-border/50 last:border-0 ${compact ? 'text-sm' : ''}`}>
        <span className="text-muted-foreground">{label}</span>
        <span className={`font-medium text-right max-w-[60%] ${compact ? 'text-sm' : ''}`}>{value}</span>
      </div>
    );
  };

  const handleAttachmentDownload = async (url) => {
    const fileName = url.split('/').pop();
    const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token') || '';
    const downloadUrl = `${BACKEND_URL}/api/files/${fileName}?token=${encodeURIComponent(token)}`;
    try {
      const res = await fetch(downloadUrl);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition');
      let realName = fileName;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) realName = match[1];
      }
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = realName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch {
      window.open(downloadUrl, '_blank');
    }
  };

  const content = (
    <div className="space-y-1">
      {/* Client Factory Visit Details */}
      {(ticket.cfv_guest_name || ticket.cfv_company || (ticket.cfv_visitors && ticket.cfv_visitors.length > 0) || (ticket.cfv_visit_locations && ticket.cfv_visit_locations.length > 0)) && (
        <>
          {/* Legacy single-guest (historical tickets) */}
          <DetailRow label="Guest Name" value={ticket.cfv_guest_name} />
          <DetailRow label="Company" value={ticket.cfv_company} />
          <DetailRow label="No. of Guests" value={ticket.cfv_guests_count} />
          <DetailRow label="Brand to Visit" value={ticket.cfv_brand} />
          <DetailRow label="Brand(s) to Visit" value={(ticket.cfv_brands && ticket.cfv_brands.length > 0) ? ticket.cfv_brands.join(', ') : ''} />
          <DetailRow label="Purpose" value={ticket.cfv_purpose} />
          <DetailRow label="Primary Purpose of Visit" value={ticket.cfv_primary_objective === 'Other' ? (ticket.cfv_primary_objective_other || 'Other') : ticket.cfv_primary_objective} />
          <DetailRow label="Linked to Project" value={ticket.cfv_project_linked} />
          <DetailRow label="Project Name" value={ticket.cfv_project_name} />
          <DetailRow label="Project Location" value={ticket.cfv_project_location} />
          <DetailRow label="Project Stage" value={ticket.cfv_project_stage} />
          <DetailRow label="Industry / Segment" value={ticket.cfv_industry_segment === 'Other' ? (ticket.cfv_industry_segment_other || 'Other') : ticket.cfv_industry_segment} />
          <DetailRow label="Area of Interest" value={ticket.cfv_area_of_interest} />
          <DetailRow label="Need Invite" value={ticket.cfv_need_invite} />
          <DetailRow label="Transport" value={ticket.cfv_transport} />
          <DetailRow label="Car Parking Required" value={ticket.cfv_car_parking} />
          <DetailRow label="Photography" value={ticket.cfv_photography} />
          <DetailRow label="Videography" value={ticket.cfv_videography} />
          <DetailRow label="Food Required" value={ticket.cfv_food_required} />
          <DetailRow label="Meals" value={(ticket.cfv_meals && ticket.cfv_meals.length > 0) ? ticket.cfv_meals.join(', ') : ''} />
          <DetailRow label="Dietary Preference" value={ticket.cfv_dietary} />
          <DetailRow label="Stay Required" value={ticket.cfv_stay_required} />
          <DetailRow label="Number of Nights" value={ticket.cfv_stay_required === 'Yes' ? ticket.cfv_stay_days : ''} />
          <DetailRow label="Giveaway / Momento" value={(ticket.cfv_giveaway && ticket.cfv_giveaway.length > 0) ? ticket.cfv_giveaway.join(', ') : ''} />
          <DetailRow label="Giveaway (Other)" value={ticket.cfv_giveaway_other} />
          <DetailRow label="Areas / Facilities to be covered" value={ticket.cfv_areas_covered} />
          <DetailRow label="Products / Solutions to be demonstrated" value={ticket.cfv_products_demonstrated} />
          <DetailRow label="Lab Testing / Demonstration" value={ticket.cfv_lab_testing} />
          <DetailRow label="Lab Testing Details" value={ticket.cfv_lab_testing_details} />
          <DetailRow label="Unitile Team Members Required" value={ticket.cfv_team_members} />
          <DetailRow label="Senior Management Presence" value={ticket.cfv_senior_mgmt} />
          <DetailRow label="Presentation Required" value={ticket.cfv_presentation} />
          <DetailRow label="Type of Presentation" value={ticket.cfv_presentation_type} />
          <DetailRow label="Conference Room Required" value={ticket.cfv_conference_room} />
          {ticket.cfv_visitors && ticket.cfv_visitors.length > 0 && (
            <div className="pt-3" data-testid="cfv-detail-visitors">
              <p className="text-sm font-semibold mb-2">Visitors ({ticket.cfv_visitors.length})</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="py-1.5 pr-3">Name</th><th className="py-1.5 pr-3">Contact</th><th className="py-1.5 pr-3">Email</th>
                      <th className="py-1.5 pr-3">Company</th><th className="py-1.5 pr-3">Designation</th><th className="py-1.5 pr-3">Country</th>{ticket.cfv_food_required === 'Yes' && <th className="py-1.5 pr-3">Dietary</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {ticket.cfv_visitors.map((v, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1.5 pr-3 font-medium">{v.name || '—'}</td>
                        <td className="py-1.5 pr-3">{v.contact || '—'}</td>
                        <td className="py-1.5 pr-3">{v.email || '—'}</td>
                        <td className="py-1.5 pr-3">{v.company || '—'}</td>
                        <td className="py-1.5 pr-3">{v.designation || '—'}</td>
                        <td className="py-1.5 pr-3">{v.country || '—'}</td>
                        {ticket.cfv_food_required === 'Yes' && <td className="py-1.5 pr-3">{v.dietary || '—'}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {ticket.cfv_visit_locations && ticket.cfv_visit_locations.length > 0 && (
            <div className="pt-3" data-testid="cfv-detail-itinerary">
              <p className="text-sm font-semibold mb-2">Visit Itinerary</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="py-1.5 pr-3">Location</th><th className="py-1.5 pr-3">Brand</th><th className="py-1.5 pr-3">Entity</th>
                      <th className="py-1.5 pr-3">Arrival</th><th className="py-1.5 pr-3">Departure</th><th className="py-1.5 pr-3">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticket.cfv_visit_locations.map((s, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1.5 pr-3 font-medium">{s.location}</td>
                        <td className="py-1.5 pr-3">{s.brand || '—'}</td>
                        <td className="py-1.5 pr-3">{s.entity || '—'}</td>
                        <td className="py-1.5 pr-3">{s.arrival_date || '—'} {s.expected_time ? formatTime(s.expected_time) : ''}</td>
                        <td className="py-1.5 pr-3">{s.departure_date || '—'} {s.departure_time ? formatTime(s.departure_time) : ''}</td>
                        <td className="py-1.5 pr-3">{s.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Courier Details */}
      {ticket.courier_type && (
        <>
          <DetailRow label="Courier Type" value={ticket.courier_type} />
          <DetailRow label="From" value={ticket.courier_from} />
          <DetailRow label="Pickup Point" value={ticket.courier_pickup_point} />
          <DetailRow label="To" value={ticket.courier_to} />
          <DetailRow label="Contents" value={ticket.courier_contents} />
        </>
      )}

      {/* Guest Arrival Details */}
      {ticket.guest_name && (
        <>
          <DetailRow label="Guest Name" value={ticket.guest_name} />
          <DetailRow label="Company" value={ticket.guest_company} />
          <DetailRow label="Purpose" value={ticket.guest_purpose} />
          <DetailRow label="Arrival Date" value={ticket.guest_arrival_date || ticket.guest_date} />
          <DetailRow label="Departure Date" value={ticket.guest_departure_date} />
          <DetailRow label="Time" value={formatTime(ticket.guest_time)} />
          <DetailRow label="No. of Guests" value={ticket.guest_count} />
          <DetailRow label="Food Required" value={ticket.guest_food_required} />
          <DetailRow label="Car Parking" value={ticket.guest_car_parking} />
          <DetailRow label="Vehicle Number" value={ticket.guest_vehicle_number} />
        </>
      )}

      {/* Event Details */}
      {ticket.event_name && (
        <>
          <DetailRow label="Event Name" value={ticket.event_name} />
          <DetailRow label="Date" value={ticket.event_date} />
          <DetailRow label="Time" value={ticket.event_time} />
          <DetailRow label="Venue" value={ticket.event_venue} />
          <DetailRow label="Location" value={ticket.event_location_dropdown || ticket.event_location} />
          <DetailRow label="Attendees" value={ticket.event_attendees} />
          <DetailRow label="Gift" value={ticket.event_gift} />
          {ticket.event_gift === 'Yes' && <DetailRow label="Per Person Gift Cost" value={ticket.event_gift_cost} />}
          <DetailRow label="Food Arrangement" value={ticket.event_food_arrangement} />
          {ticket.event_food_arrangement === 'Yes' && <DetailRow label="Per Person Budget" value={ticket.event_food_budget} />}
          <DetailRow label="Requirements" value={ticket.event_requirements} />
        </>
      )}

      {/* Room Booking Details */}
      {ticket.room_type && (
        <>
          <DetailRow label="Location" value={ticket.room_location} />
          <DetailRow label="Room" value={ticket.room_type} />
          <DetailRow label="Date" value={ticket.room_date} />
          <DetailRow label="Start Time" value={formatTime(ticket.room_start_time)} />
          <DetailRow label="End Time" value={formatTime(ticket.room_end_time)} />
          <DetailRow label="Attendees" value={ticket.room_attendees} />
          <DetailRow label="Meeting Category" value={ticket.room_meeting_category} />
          <DetailRow label="Requirements" value={ticket.room_requirements} />
        </>
      )}

      {/* Stationery Request Details */}
      {(ticket.stationery_type || ticket.stationery_items || ticket.stationery_selected_items || ticket.stationery_items_list) && (
        <>
          <DetailRow label="Request Type" value={ticket.stationery_type} />
          {ticket.stationery_items && typeof ticket.stationery_items === 'string' && (
            <>
              <DetailRow label="Items" value={ticket.stationery_items} />
              <DetailRow label="Quantity" value={ticket.stationery_quantity} />
            </>
          )}
          {(ticket.stationery_selected_items || ticket.stationery_items_list) && (
            <div className="py-2">
              <span className="text-muted-foreground">Items:</span>
              <div className="mt-1 space-y-1">
                {(ticket.stationery_items_list || ticket.stationery_selected_items || []).map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-muted/50 px-2 py-1 rounded text-sm">
                    <span>{item.name}</span>
                    <Badge variant="secondary">x{item.quantity}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DetailRow label="Description" value={ticket.stationery_description} />
          <DetailRow label="Required By" value={ticket.stationery_required_by_date} />
        </>
      )}

      {/* HR Induction / Onboarding Details */}
      {ticket.induction_employee_name && (
        <>
          <div className="text-sm font-semibold text-primary mt-2 mb-1">Basic Details</div>
          <DetailRow label="Employee Name" value={ticket.induction_employee_name} />
          <DetailRow label="Employee ID" value={ticket.induction_employee_id} />
          <DetailRow label="Designation" value={ticket.induction_designation} />
          <DetailRow label="Joining Date" value={ticket.induction_joining_date} />
          <DetailRow label="Location" value={ticket.induction_location} />
          <DetailRow label="Department" value={ticket.induction_department} />
          <DetailRow label="Employee Type" value={ticket.induction_employee_type} />
          <DetailRow label="T-Shirt Size" value={ticket.induction_tshirt_size} />
          <DetailRow label="Entity" value={ticket.induction_entity} />
          <DetailRow label="Food Required" value={ticket.induction_food_required} />


          
          {ticket.induction_need_id_card === 'yes' && (
            <>
              <div className="text-sm font-semibold text-primary mt-3 mb-1">ID Card Details</div>
              <DetailRow label="Date of Birth" value={ticket.induction_dob} />
              <DetailRow label="Date of Issue" value={ticket.induction_date_of_issue} />
              <DetailRow label="Blood Group" value={ticket.induction_blood_group} />
              <DetailRow label="Emergency Contact" value={ticket.induction_emergency_number} />
              <DetailRow label="Aadhaar Number" value={ticket.induction_masked_aadhaar ? `XXXX-XXXX-${ticket.induction_masked_aadhaar}` : ''} />
              <DetailRow label="Office Address" value={ticket.induction_office_address} />
            </>
          )}
          
          {ticket.induction_need_mediclaim === 'yes' && ticket.induction_mediclaim_members?.length > 0 && (
            <>
              <div className="text-sm font-semibold text-primary mt-3 mb-1">Mediclaim Members</div>
              {ticket.induction_mediclaim_members.map((member, idx) => (
                <div key={idx} className="bg-muted/50 p-2 rounded-md mb-2">
                  <p className="font-medium text-sm">{member.full_name || member.name || 'N/A'}</p>
                  <p className="text-xs text-muted-foreground">
                    {member.relation}{member.dob ? ` • DOB: ${member.dob}` : ''}
                  </p>
                  {(member.doj || member.grade || member.sum_insured) && (
                    <p className="text-xs text-muted-foreground">
                      {member.doj ? `DOJ: ${member.doj}` : ''}
                      {member.grade ? `${member.doj ? ' • ' : ''}Grade: ${member.grade}` : ''}
                      {member.sum_insured ? `${(member.doj || member.grade) ? ' • ' : ''}Sum Insured: ₹${member.sum_insured}` : ''}
                    </p>
                  )}
                </div>
              ))}
            </>
          )}

          {(ticket.induction_facility_visiting_card || ticket.induction_facility_hotel || ticket.induction_facility_transport) && (
            <>
              <div className="text-sm font-semibold text-primary mt-3 mb-1">Facility Requirements</div>
              <div className="flex flex-wrap gap-2">
                {ticket.induction_facility_visiting_card && <Badge variant="secondary" className="text-xs">Visiting Card</Badge>}
                {ticket.induction_facility_hotel && <Badge variant="secondary" className="text-xs">Hotel</Badge>}
                {ticket.induction_facility_transport && <Badge variant="secondary" className="text-xs">Transport</Badge>}
              </div>
            </>
          )}
        </>
      )}

      {/* Promotion Box Details */}
      {(ticket.promo_employee_name || (ticket.promo_employees && ticket.promo_employees.length > 0) || ticket.promo_entity) && (
        <>
          <div className="text-sm font-semibold text-primary mt-2 mb-1">Promotion Box Request</div>
          <DetailRow label="Entity" value={ticket.promo_entity} />
          {ticket.promo_employees && ticket.promo_employees.length > 0 ? (
            ticket.promo_employees.map((emp, idx) => (
              <div key={idx} className="bg-muted/50 p-2 rounded-md mb-2">
                <p className="font-medium text-sm">{emp.employee_name} ({emp.employee_code})</p>
                <p className="text-xs text-muted-foreground">Address: {emp.employee_address}</p>
                <p className="text-xs text-muted-foreground">Receiver: {emp.receiver_name} • {emp.receiver_address} • {emp.receiver_contact}</p>
              </div>
            ))
          ) : (
            <>
              <DetailRow label="Employee Name" value={ticket.promo_employee_name} />
              <DetailRow label="Designation" value={ticket.promo_designation} />
              <DetailRow label="Department" value={ticket.promo_department} />
              <DetailRow label="Effective Date" value={ticket.promo_effective_date} />
              <DetailRow label="Special Instructions" value={ticket.promo_special_instructions} />
            </>
          )}
        </>
      )}

      {/* Desk Decor - Birthday Details */}
      {(ticket.birthday_employee_name || (ticket.birthday_employees && ticket.birthday_employees.length > 0)) && (
        <>
          <div className="text-sm font-semibold text-primary mt-2 mb-1">Birthday Celebration</div>
          {ticket.birthday_employees && ticket.birthday_employees.length > 0 ? (
            ticket.birthday_employees.map((emp, idx) => (
              <div key={idx} className="bg-muted/50 p-2 rounded-md mb-2">
                <p className="font-medium text-sm">{emp.employee_name}</p>
                <p className="text-xs text-muted-foreground">Date: {emp.required_by_date} • Dept: {emp.department}</p>
              </div>
            ))
          ) : (
            <>
              <DetailRow label="Employee Name" value={ticket.birthday_employee_name} />
              <DetailRow label="Birthday Date" value={ticket.birthday_date} />
              <DetailRow label="Department" value={ticket.birthday_department} />
              <DetailRow label="Location" value={ticket.birthday_location} />
            </>
          )}
        </>
      )}

      {/* Insurance Addition Details */}
      {ticket.insurance_add_employees && ticket.insurance_add_employees.length > 0 && (
        <>
          <div className="text-sm font-semibold text-primary mt-2 mb-1">Insurance Addition</div>
          {ticket.insurance_add_employees.map((emp, idx) => (
            <div key={idx} className="bg-muted/50 p-2 rounded-md mb-2">
              <p className="font-medium text-sm">{emp.employee_name} ({emp.employee_code})</p>
              <p className="text-xs text-muted-foreground">
                {emp.department} • Grade: {emp.grade} • Sum Insured: {emp.sum_insured}
              </p>
              <p className="text-xs text-muted-foreground">
                For: {emp.insurance_required_for} • Name: {emp.member_name} • DOB: {emp.member_dob}
              </p>
            </div>
          ))}
        </>
      )}

      {/* Insurance Removal Details */}
      {(ticket.insurance_employee_name || (ticket.insurance_employees && ticket.insurance_employees.length > 0)) && (
        <>
          <div className="text-sm font-semibold text-primary mt-2 mb-1">Insurance Removal</div>
          {ticket.insurance_employees && ticket.insurance_employees.length > 0 ? (
            ticket.insurance_employees.map((emp, idx) => (
              <div key={idx} className="flex justify-between items-center bg-muted/50 px-2 py-1 rounded text-sm mb-1">
                <span>{emp.name} ({emp.id})</span>
                <Badge variant="secondary">Exit: {emp.exit_date}</Badge>
              </div>
            ))
          ) : (
            <>
              <DetailRow label="Employee Name" value={ticket.insurance_employee_name} />
              <DetailRow label="Employee ID" value={ticket.insurance_employee_id} />
              <DetailRow label="Date of Exit" value={ticket.insurance_exit_date} />
            </>
          )}
        </>
      )}

      {/* Repairs & Maintenance Details */}
      {ticket.maintenance_category && (
        <>
          <div className="text-sm font-semibold text-primary mt-2 mb-1">Repairs & Maintenance</div>
          <DetailRow label="Category" value={ticket.maintenance_category} />
          {ticket.maintenance_other_text && <DetailRow label="Details" value={ticket.maintenance_other_text} />}
        </>
      )}

      {/* Other Request Type */}
      {ticket.other_request_type && (
        <DetailRow label="Custom Request Type" value={ticket.other_request_type} />
      )}

      {/* Custom Fields */}
      {ticket.custom_fields && Object.keys(ticket.custom_fields).length > 0 && (
        <>
          <div className="text-sm font-semibold text-primary mt-2 mb-1">Custom Details</div>
          {Object.entries(ticket.custom_fields).map(([key, value]) => (
            <DetailRow 
              key={key} 
              label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} 
              value={typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value} 
            />
          ))}
        </>
      )}

      {/* Departments to be Informed (SPOC) — Guest Arrival / Client Factory Visit */}
      {ticket.informed_departments && ticket.informed_departments.length > 0 && (
        <DetailRow label="Departments to be Informed" value={ticket.informed_departments.join(', ')} />
      )}

      {/* Attachments */}
      {ticket.attachments?.length > 0 && (
        <div className="pt-3 mt-2 border-t border-border/50">
          <div className="flex items-center gap-2 mb-2">
            <Download className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">Attachments ({ticket.attachments.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {ticket.attachments.map((url, index) => {
              const fileName = url.split('/').pop();
              return (
                <button
                  key={index}
                  onClick={() => handleAttachmentDownload(url)}
                  className="flex items-center gap-1.5 bg-muted px-3 py-1.5 rounded-md cursor-pointer hover:bg-muted/80 transition-colors"
                >
                  <Download className="w-3 h-3 text-primary" />
                  <span className="text-xs text-primary">{fileName}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  if (compact) {
    return (
      <div className="bg-muted/50 rounded-lg p-4 max-h-[400px] overflow-y-auto">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Request Details</span>
        </div>
        {content}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Request Details
        </CardTitle>
        <CardDescription>
          Additional information for this {ticket.request_type} request
        </CardDescription>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};

export default RequestDetailsSection;
